import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoginButton } from "../../features/auth";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { ThemeSwitcher } from "../ThemeSwitcher";
import { MessageSquare, ArrowLeftRight, Wallet } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Header Component
 * Branded navigation header with Solana authentication.
 *
 * Nav labels come from i18n in both locales (`nav.chat`, `nav.dashboard`,
 * `nav.strategies`, `nav.settings`). The notification bell that used to sit
 * beside the language switcher is gone: its count lived in `ui-store` and
 * nothing ever incremented it — a figure on screen the wire does not carry.
 * A decision that waits on the person is drawn where it is counted (the
 * conversation row and the chat context panel), never as a global badge.
 */
export function Header() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const zh = i18n.language.startsWith("zh");
  const isActive = (path: string): boolean => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: "/chat", label: zh ? "聊天" : "Chat", icon: <MessageSquare className="h-5 w-5" /> },
    { path: "/trade", label: zh ? "交易" : "Trade", icon: <ArrowLeftRight className="h-5 w-5" /> },
    { path: "/assets", label: zh ? "资产" : "Assets", icon: <Wallet className="h-5 w-5" /> },
  ];

  return (
    <>
      <header className="border-b border-outline-variant/20 bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/" className="group shrink-0" aria-label="DeTrust home">
              {/* Two generated marks, one per ground: the wordmark is dark ink,
                  so on the shell's dark surfaces it disappears. The theme picks
                  (dt-mark--light / --dark in vnext.css), the same way every
                  token does — never a filter or a hand-edited asset. */}
              <img
                src="/detrust-logo-compact.svg"
                alt="DeTrust AI"
                className="dt-mark--light h-7 w-auto"
              />
              <img
                src="/detrust-logo-compact-white.svg"
                alt=""
                aria-hidden="true"
                className="dt-mark--dark h-7 w-auto"
              />
            </Link>

            {/* Separator */}
            <div className="h-6 border-l border-outline-variant/30 mx-5" />

            {/* Navigation -- centered in available space */}
            <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium transition-colors relative",
                      active
                        ? "text-primary font-semibold border-b-2 border-primary pb-1"
                        : "text-on-surface/70 hover:text-primary",
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Separator */}
            <div className="h-6 border-l border-outline-variant/30 mx-5 hidden md:block" />

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0 ml-auto md:ml-0">
              {/* Appearance beside language: both are per-device preferences
                  about the person reading the screen (spec 2026-09-07 D18). */}
              <ThemeSwitcher />
              <LanguageSwitcher />

              <div className="pl-2.5 ml-1 border-l border-outline-variant/30">
                <LoginButton size="default" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile bottom tab bar */}
      <footer className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-t border-outline-variant/20">
        <nav className="flex items-stretch justify-around px-2 py-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors rounded-lg min-w-[60px]",
                  active
                    ? "text-primary"
                    : "text-on-surface-variant/60 hover:text-on-surface",
                )}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </footer>
    </>
  );
}
